import { useEffect, useState } from 'react';
import type { ClientSession } from '../api';
import { Loading } from '../components/Status';

import type {
  FeatureMode as Mode,
  FeatureState as Feature,
  FeaturesResponse,
} from '../../shared/features';
const label: Record<Mode, string> = { off: 'Off', internal: 'Internal', on: 'On' };
async function featureRequest(path: string, session: ClientSession, body?: unknown) {
  const response = await fetch(path, {
    method: body ? 'PATCH' : 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(session.csrfToken ? { 'X-CSRF-Token': session.csrfToken } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = (await response.json()) as FeaturesResponse & { error?: string };
  if (!response.ok) throw new Error(result.error || 'Feature settings could not be saved');
  return result;
}
function FeatureCard({
  feature,
  session,
  onSaved,
}: {
  feature: Feature;
  session: ClientSession;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState(feature.mode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => setMode(feature.mode), [feature.mode, feature.version]);
  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await featureRequest(`/api/admin/features/${encodeURIComponent(feature.key)}`, session, {
        mode,
        version: feature.version,
        confirm: true,
      });
      await onSaved();
      setNotice(
        `Saved ${label[mode]}. Availability also depends on the deployment limit and feature readiness.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Feature settings could not be saved');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="settings-card" data-testid={`feature-${feature.key}`}>
      <h2>{feature.name}</h2>
      <p>{feature.description}</p>
      <p>
        <strong>Effective: {label[feature.effectiveMode]}</strong> · Deployment limit:{' '}
        {label[feature.deploymentMode]}
      </p>
      <p>Readiness: {feature.status}</p>
      <p>Requires: {feature.dependencies.join(', ')}.</p>
      {!feature.implemented && (
        <p>Planned feature. Saving a rollout setting does not make it available yet.</p>
      )}
      {feature.blockedReasons.length > 0 && (
        <ul>
          {feature.blockedReasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
      <label className="field">
        <span>Rollout setting for {feature.name}</span>
        <select
          aria-label={`Rollout setting for ${feature.name}`}
          value={mode}
          disabled={busy}
          onChange={(event) => {
            setMode(event.target.value as Mode);
            setNotice('');
          }}
        >
          <option value="off">Off</option>
          <option value="internal">Internal (staff pilot)</option>
          <option value="on">On</option>
        </select>
      </label>
      {mode !== feature.mode && (
        <p>
          {mode === 'off'
            ? 'Pauses new activity without deleting stored records.'
            : mode === 'internal'
              ? 'Limits participation to supported staff pilot flows. Unsupported pilots remain off.'
              : 'Allows authorized participants only when deployment and service requirements are ready.'}
        </p>
      )}
      <button
        type="button"
        className="button"
        disabled={busy || mode === feature.mode}
        onClick={() => void save()}
      >
        {busy ? 'Saving…' : 'Apply change'}
      </button>
      {error && (
        <p role="alert" className="inline-error">
          {error} Reload the page to review the latest setting before retrying.
        </p>
      )}
      {notice && (
        <p role="status" className="inline-success">
          {notice}
        </p>
      )}
    </section>
  );
}
export function FeaturesPage({ session }: { session: ClientSession | null }) {
  const [state, setState] = useState<FeaturesResponse | null>(null);
  const [error, setError] = useState('');
  const allowed = !!session?.capabilities.superAdmin;
  const load = async () => {
    if (!session || !allowed) return;
    setState(await featureRequest('/api/admin/features', session));
  };
  useEffect(() => {
    if (allowed) void load().catch((cause) => setError(cause.message));
  }, [allowed]);
  if (!allowed)
    return (
      <main id="main-content" className="center-page">
        <h1>Super Admin access required</h1>
        <p>Only a Super Admin can change feature rollout.</p>
        <a href="/admin">Back to administration</a>
      </main>
    );
  return (
    <main id="main-content" className="settings-page">
      <a className="back-link" href="/admin">
        ← Administration
      </a>
      <h1>Features</h1>
      <p>
        Choose Off, Internal or On for each feature. Calendar permissions and staff-domain
        restrictions always apply.
      </p>
      <p>
        Settings are saved in this app. Cloudflare Flagship sets the deployment limit. Features that
        are not built or configured remain unavailable.
      </p>
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {!state && !error && <Loading label="Loading feature settings" />}
      {state && (
        <>
          <p>
            <b>{state.environment === 'local' ? 'Local testing only' : 'Deployment settings'}</b>.
            Every change is audited.
          </p>
          {state.features.map((feature) => (
            <FeatureCard key={feature.key} feature={feature} session={session!} onSaved={load} />
          ))}
          <section className="settings-card">
            <h2>Recent changes</h2>
            {state.changelog.length === 0 ? (
              <p>No feature changes yet.</p>
            ) : (
              <ul>
                {state.changelog.map((change) => (
                  <li key={change.id}>
                    {state.features.find((feature) => feature.key === change.featureKey)?.name ||
                      change.featureKey}
                    : {label[change.before]} → {label[change.after]}, by{' '}
                    {change.actorName || 'Former account'} on{' '}
                    {new Date(change.createdAt).toLocaleString()}.
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
